// Shared types, constants and helpers for the PulseConnect survey platform.
import { supabase } from '../services/supabase';

export const C = {
  navy: '#0A1628', navyHover: '#111E35', gold: '#C9A84C', black: '#0A0A0A',
  white: '#FFFFFF', surface: '#F5F6F8', border: '#D0D5DD', borderLt: '#E8EBF0',
  text: '#0A0A0A', textSec: '#3D4552', muted: '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger: '#C0222A', amber: '#92640A', teal: '#0D6E8A',
};

// ── Types ──────────────────────────────────────────────────────────────────
export type QuestionType =
  | 'multiple_choice' | 'multi_select' | 'rating' | 'nps'
  | 'free_text' | 'ranking' | 'matrix' | 'grid';

export type SurveyStatus = 'draft' | 'published' | 'closed';
export type TargetAudience = 'managers' | 'allocators' | 'both' | 'specific';

export interface PulseSurvey {
  id: string;
  created_by: string | null;
  title: string;
  description: string | null;
  target_audience: TargetAudience;
  target_profile_ids: string[] | null;
  status: SurveyStatus;
  deadline: string | null;
  results_published: boolean;
  results_published_at: string | null;
  show_results_after_completion: boolean;
  template_type: string | null;
  conference_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface PulseQuestion {
  id: string;
  survey_id: string;
  order_index: number;
  question_type: QuestionType;
  question_text: string;
  description: string | null;
  required: boolean;
  options: string[];
  matrix_rows: string[];
  matrix_columns: string[];
  rating_min: number;
  rating_max: number;
  rating_min_label: string | null;
  rating_max_label: string | null;
  created_at?: string;
}

export interface PulseAssignment {
  id: string;
  survey_id: string;
  profile_id: string;
  assigned_at: string;
  notified_at: string | null;
  reminder_sent_at: string | null;
}

export interface PulseResponse {
  id: string;
  survey_id: string;
  profile_id: string | null;
  started_at: string;
  completed_at: string | null;
  is_complete: boolean;
  firm_strategy: string | null;
  firm_aum_range: string | null;
  firm_hq_region: string | null;
  created_at: string;
  updated_at: string;
}

export interface PulseAnswer {
  id: string;
  response_id: string;
  question_id: string;
  answer_text: string | null;
  answer_number: number | null;
  answer_options: string[];
  answer_matrix: Record<string, string>; // { [rowLabel]: chosen column label }
}

export interface PulseTemplate {
  id: string;
  name: string;
  description: string | null;
  template_type: string;
  questions: Partial<PulseQuestion>[];
  created_at: string;
}

// A local, in-progress answer value keyed by question id while taking a survey.
export interface AnswerValue {
  text?: string;
  number?: number | null;
  options?: string[];
  matrix?: Record<string, string>; // { [rowLabel]: chosen column label }
}

// ── Constants ────────────────────────────────────────────────────────────────
export const QUESTION_TYPES: { value: QuestionType; label: string; hint: string }[] = [
  { value: 'multiple_choice', label: 'Multiple Choice', hint: 'Single selection from a list' },
  { value: 'multi_select',    label: 'Multi-Select',    hint: 'Choose several options' },
  { value: 'rating',          label: 'Rating',          hint: 'Numbered / star scale' },
  { value: 'nps',             label: 'NPS',             hint: '0–10 recommendation score' },
  { value: 'free_text',       label: 'Free Text',       hint: 'Open written response' },
  { value: 'ranking',         label: 'Ranking',         hint: 'Order items by preference' },
  { value: 'matrix',          label: 'Matrix / Grid',   hint: 'Rate rows across columns' },
];

export const AUDIENCE_OPTIONS: { value: TargetAudience; label: string }[] = [
  { value: 'managers',   label: 'All Managers' },
  { value: 'allocators', label: 'All Allocators' },
  { value: 'both',       label: 'Both' },
  { value: 'specific',   label: 'Specific Individuals' },
];

// Demographic dimensions used for the results filter panel.
export const STRATEGY_OPTIONS = [
  'Equity Long/Short', 'Global Macro', 'Credit', 'Multi-Strategy', 'Quant / Systematic',
  'Event Driven', 'Fixed Income', 'Private Equity', 'Venture Capital', 'Real Estate',
  'Infrastructure', 'Fund of Funds', 'Other',
];

export const AUM_RANGES = [
  'Under $100M', '$100M–$500M', '$500M–$1B', '$1B–$5B', '$5B–$10B', 'Over $10B',
];

export const REGION_OPTIONS = [
  'North America', 'Europe', 'UK', 'Middle East', 'Asia-Pacific', 'Latin America', 'Africa',
];

export const NPS_COLORS = ['#C0222A', '#C0222A', '#D14A24', '#D14A24', '#DB7A1E', '#DB7A1E',
  '#C9A84C', '#C9A84C', '#5B9E4A', '#2E8B3D', '#0A7C42'];

// ── Small helpers ────────────────────────────────────────────────────────────
export function npsCategory(n: number): 'promoter' | 'passive' | 'detractor' {
  if (n >= 9) return 'promoter';
  if (n >= 7) return 'passive';
  return 'detractor';
}

export function npsColor(n: number): string {
  return NPS_COLORS[Math.max(0, Math.min(10, n))];
}

// Rough completion estimate: ~1 minute per 3 questions, min 1 minute.
export function estimateMinutes(questionCount: number): number {
  return Math.max(1, Math.ceil(questionCount / 3));
}

export interface DeadlineInfo { text: string; color: string; urgent: boolean; overdue: boolean; }

export function deadlineInfo(deadline: string | null): DeadlineInfo | null {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - Date.now();
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor(ms / 3_600_000);
  if (ms <= 0) return { text: 'Deadline passed', color: C.danger, urgent: true, overdue: true };
  if (days < 2) {
    const label = hours < 24 ? `${Math.max(1, hours)} hour${hours === 1 ? '' : 's'} remaining` : '1 day remaining';
    return { text: label, color: C.danger, urgent: true, overdue: false };
  }
  if (days < 7) return { text: `${days} days remaining`, color: C.amber, urgent: true, overdue: false };
  return { text: `${days} days remaining`, color: C.muted, urgent: false, overdue: false };
}

export function fmtDate(ts: string | null): string {
  return ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
}

export function fmtDateTime(ts: string | null): string {
  return ts ? new Date(ts).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
}

// Is a required question answered by the given local value?
export function isAnswered(q: PulseQuestion, v: AnswerValue | undefined): boolean {
  if (!v) return false;
  switch (q.question_type) {
    case 'multiple_choice':
    case 'free_text':
      return q.question_type === 'free_text' ? !!(v.text && v.text.trim()) : !!(v.text && v.text.trim());
    case 'rating':
    case 'nps':
      return v.number !== undefined && v.number !== null;
    case 'multi_select':
      return !!(v.options && v.options.length > 0);
    case 'ranking':
      return !!(v.options && v.options.length === q.options.length);
    case 'matrix':
    case 'grid':
      return q.matrix_rows.every(r => !!(v.matrix && v.matrix[r]));
    default:
      return false;
  }
}

// Normalise a raw DB question row (jsonb columns may be null) into PulseQuestion.
export function normalizeQuestion(row: any): PulseQuestion {
  return {
    id: row.id,
    survey_id: row.survey_id,
    order_index: row.order_index,
    question_type: row.question_type,
    question_text: row.question_text,
    description: row.description ?? null,
    required: row.required ?? true,
    options: Array.isArray(row.options) ? row.options : [],
    matrix_rows: Array.isArray(row.matrix_rows) ? row.matrix_rows : [],
    matrix_columns: Array.isArray(row.matrix_columns) ? row.matrix_columns : [],
    rating_min: row.rating_min ?? 1,
    rating_max: row.rating_max ?? 5,
    rating_min_label: row.rating_min_label ?? null,
    rating_max_label: row.rating_max_label ?? null,
    created_at: row.created_at,
  };
}

// ── Notifications ────────────────────────────────────────────────────────────
// The notifications table uses (user_id, type, title, body, link).
interface NotifRow { user_id: string; type: string; title: string; body: string; link: string; }

export async function notifyUsers(rows: NotifRow[]): Promise<void> {
  if (!rows.length) return;
  await supabase.from('notifications').insert(rows);
}

export async function notifySurveyPublished(survey: PulseSurvey, profileIds: string[]): Promise<void> {
  const deadline = survey.deadline ? `, deadline ${fmtDate(survey.deadline)}` : '';
  await notifyUsers(profileIds.map(id => ({
    user_id: id,
    type: 'survey_assigned',
    title: 'New survey assigned',
    body: `${survey.title}${deadline}`,
    link: '/pulse-connect',
  })));
}

export async function notifyResultsPublished(survey: PulseSurvey, profileIds: string[]): Promise<void> {
  await notifyUsers(profileIds.map(id => ({
    user_id: id,
    type: 'survey_results',
    title: 'Survey results available',
    body: survey.title,
    link: `/pulse-connect/results/${survey.id}`,
  })));
}

export async function notifyReminder(survey: PulseSurvey, profileIds: string[]): Promise<void> {
  await notifyUsers(profileIds.map(id => ({
    user_id: id,
    type: 'survey_reminder',
    title: 'Survey closing soon',
    body: `${survey.title} closes ${survey.deadline ? fmtDate(survey.deadline) : 'soon'}, you have not yet responded`,
    link: '/pulse-connect',
  })));
}

// ── AI theme summary (PB only) ───────────────────────────────────────────────
// Summarise free-text answers into key themes via the altveno-ai edge function.
export async function summarizeThemes(questionText: string, answers: string[]): Promise<string> {
  const clean = answers.map(a => (a || '').trim()).filter(Boolean);
  if (clean.length === 0) return 'No free-text responses to summarise yet.';
  const systemPrompt =
    `You are a research analyst summarising open-text survey responses for an alternative investment platform. ` +
    `Read the responses to the question and produce a concise summary of the key recurring themes (3–5 bullet points, each one short sentence). ` +
    `Be specific and neutral. Do not invent themes not present. Question: "${questionText}".`;
  const { data, error } = await supabase.functions.invoke('altveno-ai', {
    body: {
      systemPrompt,
      messages: [{ role: 'user', content: `Here are ${clean.length} responses:\n\n${clean.map((a, i) => `${i + 1}. ${a}`).join('\n')}\n\nSummarise the key themes.` }],
    },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data?.content?.[0]?.text || 'No summary returned.';
}

// ── CSV export ───────────────────────────────────────────────────────────────
export function toCSV(rows: (string | number | null)[][]): string {
  return rows.map(r => r.map(cell => {
    const s = cell === null || cell === undefined ? '' : String(cell);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(',')).join('\r\n');
}

export function downloadCSV(filename: string, csv: string): void {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
